import { type FC, type ReactElement } from 'react';
import {
	StyleSheet,
	Switch,
	Text,
	TouchableOpacity,
	View,
	type GestureResponderEvent,
	type ViewStyle,
} from 'react-native';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import { type SvgIconType } from '@thermenresorts/icons';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';

interface Props {
	label: string;
	icon?: SvgIconType;
	iconSize?: number;
	color?: string;
	extraStyle?: ViewStyle;
	detailText?: string;
	switch?: boolean;
	value?: boolean;
	onValueChange?: (value: boolean) => void;
	onPress?: (event: GestureResponderEvent) => void;
	last?: boolean;
	imageComponent?: ReactElement;
	badge?: number;
}

const MenuRow: FC<Props> = (props) => {
	const lastStyle = !props.last ? styles.borderBottom : {};
	const style = { ...styles.menuitem, ...lastStyle, ...props.extraStyle };
	const color = props.color ?? Colors.gray800;

	const { icon, iconSize = Dimens.iconSize18 } = props;

	return (
		<TouchableOpacity onPress={props.onPress}>
			<View style={style}>
				<View style={[styles.row, !props.detailText && styles.rowCentered]}>
					{props.imageComponent ? <View style={styles.iconSlot}>{props.imageComponent}</View> : null}
					{icon && (
						<View style={styles.iconSlot}>
							<SvgIcon name={icon} size={iconSize} tintColor={color} />
						</View>
					)}
					<View style={styles.rowBetween}>
						<View style={styles.column}>
							<Text ellipsizeMode="head" numberOfLines={1} style={[styles.label, { color }]}>
								{props.label}
							</Text>
							{props.detailText ? <Text style={styles.detailText}>{props.detailText}</Text> : null}
						</View>
						{props.badge && props.badge > 0 ? (
							<View style={styles.badgeContainer}>
								<Text style={styles.badgeText}>
									{props.badge > Constants.badgeMaxCount ? `${Constants.badgeMaxCount}+` : props.badge}
								</Text>
							</View>
						) : null}
						{props.switch ? (
							<View style={styles.switchContainer}>
								<Switch
									ios_backgroundColor={Colors.gray175}
									trackColor={{
										false: Colors.gray175,
										true: Colors.green700,
									}}
									value={props.value}
									onValueChange={props.onValueChange}
								/>
							</View>
						) : null}
					</View>
				</View>
			</View>
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	menuitem: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding24,
		marginHorizontal: Dimens.padding18,
	},
	borderBottom: {
		borderColor: Colors.gray200,
		borderBottomWidth: Dimens.borderWidth1,
	},
	label: {
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	detailText: {
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight23,
		color: Colors.gray500,
		marginTop: Dimens.padding10,
		width: '80%',
	},
	row: {
		flexDirection: 'row',
		width: '100%',
	},
	rowCentered: {
		alignItems: 'center',
	},
	rowBetween: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		flex: Dimens.flex1,
	},
	column: {
		flexDirection: 'column',
		flex: Dimens.flex1,
	},
	iconSlot: {
		width: Dimens.size24,
		alignItems: 'center',
		marginRight: Dimens.padding14,
	},
	switchContainer: {
		width: Dimens.size50,
	},
	badgeContainer: {
		backgroundColor: Colors.green,
		height: Dimens.size24,
		minWidth: Dimens.size24,
		borderRadius: Dimens.borderRadius99,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: Dimens.padding8,
	},
	badgeText: {
		color: Colors.white,
		fontSize: Dimens.fontSize13,
		fontWeight: Dimens.fontWeight700,
		textAlign: 'center',
	},
});

export default MenuRow;
